'use client'

// =============================================================================
// LoginForm — the ONE sign-in implementation for Parts4Drive
// -----------------------------------------------------------------------------
// Used by:
//   * app/login/page.tsx   (merchant / full-page login)
//   * app/page.tsx         (marketplace home modal, via the `onAuthed` callback)
//
// Both surfaces used to carry their own copy of this logic, and they drifted:
// the page sent everyone (including returning merchants) to /onboarding on Google
// sign-in and used the device-verification OTP dialog, while the home modal used
// a different OTP endpoint, kept its own local user state and never ran the
// seller/onboarding post-auth routing. One component, one flow, one OTP path.
// =============================================================================

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react'
import { authClient, isSuperAdmin, subscribeToAuth } from '@/lib/auth'
import { getOnboardingService } from '@/lib/onboarding'
import { VerifyEmailDialog } from '@/components/VerifyEmailDialog'

export interface LoginFormProps {
  /**
   * Same-origin path to return to after authentication (e.g. '/' or '/shop').
   * Only same-origin paths are honoured — anything else is ignored.
   */
  returnTo?: string
  /** Prefills the email field (e.g. `?email=` on /login). */
  defaultEmail?: string
  /**
   * When provided the form does NOT navigate: it hands the authenticated user
   * to the caller so pending in-page actions can resume (home modal garage /
   * product flows). Post-auth routing (superadmin → portal, onboarding check)
   * stays with the caller in that mode.
   */
  onAuthed?: (user: any, token?: string) => void
}

/** Accepts only same-origin absolute paths: `/x`, never `//evil.com` or `https://…`. */
function safeReturnTo(value?: string): string | null {
  if (!value) return null
  const v = value.trim()
  return v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') ? v : null
}

export function LoginForm({ returnTo, defaultEmail, onAuthed }: LoginFormProps) {
  const router = useRouter()

  const [formData, setFormData] = useState({
    email: defaultEmail || '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [showDeviceVerification, setShowDeviceVerification] = useState(false)
  const [deviceVerifyEmail, setDeviceVerifyEmail] = useState('')
  const [deviceVerifyId, setDeviceVerifyId] = useState('')
  const [deviceVerifyName, setDeviceVerifyName] = useState('')

  // Track session (so the form can react to an already-authenticated visitor)
  useEffect(() => {
    const unsubscribe = subscribeToAuth((session) => {
      setCurrentUser(session?.user || null)
    })
    return unsubscribe
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }))
    setError(null)
  }

  /** Shared exit point for email sign-in, device verification and Google sign-in. */
  const finishAuth = async (user: any, token?: string) => {
    // Embedded mode (home modal): the caller owns what happens next.
    if (onAuthed) {
      onAuthed(user, token)
      return
    }

    const target = safeReturnTo(returnTo)
    if (target) {
      // Full navigation so the edge router (packages/ai-worker) decides whether
      // the destination belongs to the marketplace or the seller SPA.
      window.location.href = target
      return
    }

    if (isSuperAdmin(user)) {
      const isProd =
        typeof window !== 'undefined' && window.location.hostname.includes('parts4drive.com')
      if (isProd) {
        window.location.href = `https://portal.parts4drive.com/admin${token ? `?token=${encodeURIComponent(token)}` : ''}`
      } else {
        router.push('/admin')
      }
      return
    }

    let needsOnboarding = true
    try {
      const res = await fetch('/api/seller/profile', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      })
      if (res.ok) {
        const p = await res.json()
        if (p?.data?.id) needsOnboarding = false
      }
    } catch {
      /* profile unavailable → assume onboarding is still required */
    }

    const destination = needsOnboarding ? '/onboarding' : '/dashboard'
    if (typeof window !== 'undefined' && window.location.hostname.includes('parts4drive.com')) {
      window.location.href = `https://parts4drive.com${destination}${token ? `?token=${encodeURIComponent(token)}` : ''}`
    } else {
      router.push(destination)
    }
  }

  const handleGoogleSignIn = () => {
    try {
      setLoading(true)
      const onboarding = getOnboardingService()
      onboarding.orgData = {
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        provider: 'google',
      }
      authClient.signInGoogle(safeReturnTo(returnTo) || '/onboarding')
    } catch (err: any) {
      setError(err?.message || 'Google sign-in could not be initiated.')
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const res = await authClient.signInEmail({
        email: formData.email,
        password: formData.password,
      })

      if (res.requiresDeviceOtp || res.requiresOtp) {
        setDeviceVerifyEmail(res.email || formData.email)
        setDeviceVerifyId(res.deviceId || '')
        setDeviceVerifyName(res.deviceName || '')
        setShowDeviceVerification(true)
        setLoading(false)
        return
      }

      if (!res.success) {
        setError(res.error || 'Invalid email or password.')
        setLoading(false)
        return
      }

      const user = res.user || res.data?.user
      if (!user) {
        throw new Error('No user profile received.')
      }

      const onboarding = getOnboardingService()
      onboarding.orgData = {
        email: user.email,
        displayName: user.name,
        provider: 'email',
      }

      await finishAuth(user, res.token || authClient.getToken())
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred during sign in.')
    } finally {
      setLoading(false)
    }
  }

  const handleDeviceVerifiedSuccess = (data: any) => {
    setShowDeviceVerification(false)
    const user = data?.user || data?.data
    const token = data?.token || data?.session?.token || authClient.getToken()
    finishAuth(user, token)
  }

  return (
    <div className="space-y-4">
      {/* Google Sign In */}
      <Button
        type="button"
        variant="outline"
        className="w-full rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold h-11"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.46l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            Continue with Google
          </>
        )}
      </Button>

      <div className="relative flex items-center gap-3">
        <Separator className="flex-1 bg-slate-200" />
        <span className="text-xs text-slate-400 font-medium shrink-0">or continue with email</span>
        <Separator className="flex-1 bg-slate-200" />
      </div>

      {/* Email/Password Form */}
      <form onSubmit={handleLogin} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-semibold text-slate-700">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              className="pl-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-xs font-semibold text-slate-700">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="pl-10 pr-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Link
            href="/reset-password"
            className="text-xs font-semibold text-[#2563EB] hover:underline cursor-pointer"
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <div className="text-xs font-medium text-rose-600 bg-rose-50 p-3 rounded-xl border border-rose-200">
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full rounded-full bg-[#0A1B4A] hover:bg-[#0f2560] text-white font-bold h-11 shadow-sm"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
        </Button>
      </form>

      <div className="text-center text-xs pt-2">
        <span className="text-slate-500">Don&apos;t have an account? </span>
        <Link href="/signup" className="text-[#2563EB] hover:underline font-bold cursor-pointer">
          Create account
        </Link>
      </div>

      <VerifyEmailDialog
        open={showDeviceVerification}
        onOpenChange={setShowDeviceVerification}
        email={deviceVerifyEmail}
        purpose="device_verification"
        deviceId={deviceVerifyId}
        deviceName={deviceVerifyName}
        onSuccess={handleDeviceVerifiedSuccess}
      />
    </div>
  )
}

export default LoginForm
